import {
  TDC_CONTACT_EMAIL,
  TDC_DONATE_URL,
  TDC_HOME_URL
} from '@/lib/external-links';

export type EngagementInterest =
  | 'give'
  | 'partner'
  | 'advocate'
  | 'navigator'
  | 'general';

export type EngagementPathway = {
  id: EngagementInterest;
  title: string;
  summary: string;
  body: string;
  /** In-page section anchor on /get-involved */
  anchor: string;
  ctaLabel: string;
  /** Internal path, hash, or external URL */
  href: string;
  external?: boolean;
  tone: 'cta' | 'primary' | 'violet';
};

export const engagementMeta = {
  contactEmail: TDC_CONTACT_EMAIL,
  donateUrl: TDC_DONATE_URL,
  tdcHomeUrl: TDC_HOME_URL
} as const;

export const interestOptions: { value: EngagementInterest; label: string }[] = [
  { value: 'give', label: 'Give to the Brain Cancer Fund' },
  { value: 'partner', label: 'Multi-year giving (individual / corporate / philanthropic)' },
  { value: 'advocate', label: 'Support advocacy / share the mission' },
  { value: 'navigator', label: 'Cancer Navigator — register interest' },
  { value: 'general', label: 'General enquiry' }
];

export const pathways: EngagementPathway[] = [
  {
    id: 'give',
    title: 'Give to the Fund',
    summary: 'Every gift, large or small, backs brain cancer research that won’t get funded otherwise.',
    body: 'Individuals, families, and everyday donors can give any amount through Tour de Cure’s established donation pathway — one-off or recurring. Every gift pools into the Brain Cancer Fund and is directed through the same independent scientific assessment process that has guided more than twenty years of cancer research funding. No minimum. No gatekeeping.',
    anchor: 'give',
    ctaLabel: 'Donate via Tour de Cure',
    href: '/donate',
    tone: 'cta'
  },
  {
    id: 'partner',
    title: 'Multi-year giving',
    summary: 'Commit to regular or multi-year donations that give researchers the certainty to plan bold work.',
    body: 'Philanthropists, family offices, and corporates can commit to multi-year donations — monthly, quarterly, or annual — that provide the stable grants pool researchers need to hire teams, run trials, and reach human-trial readiness. Every grant is assessed through Tour de Cure’s independent research committee — not against logo placement. We celebrate the generosity of every donor, at every level.',
    anchor: 'partner',
    ctaLabel: 'Start a multi-year gift',
    href: '/get-involved#register',
    tone: 'primary'
  },
  {
    id: 'advocate',
    title: 'Support advocacy',
    summary: 'Help surface an underfunded disease to the people who can change it.',
    body: 'Board members, clinicians, survivors, families, and communicator allies — amplify the mission, introduce partners, or invite NextOnVera into rooms where brain cancer rarely gets a seat.',
    anchor: 'advocate',
    ctaLabel: 'Register as an advocate',
    href: '/get-involved#register',
    tone: 'violet'
  }
];

export const fundPage = {
  documentTitle: 'The Fund · NextOnVera',
  eyebrow: 'Funding model',
  title: 'The Fund',
  lead:
    'A giving fund backed by individual philanthropists, corporates, and everyday donors — overseen by Tour De Cure’s research committee. Every gift, large or small, backs NextOnVera and early-stage brain cancer research that conventional funding won’t touch. Multi-year commitments give researchers the certainty to plan beyond a single grant cycle.',
  whyHeading: 'Why a fund, not a single grant',
  whyBody: [
    "Brain cancer survival hasn't moved in 25 years, not for lack of science, but because the system funding that science hasn't kept pace with how complex the disease is.",
    'NextOnVera funds the gap between an early insight and the evidence needed to act on it — the high-risk, early-stage, cross-institution research that conventional funding routes won’t touch. By pooling gifts from individuals, corporates, and philanthropists into a single research grants pool, we give promising ideas the runway they need to reach human trials.'
  ],
  modelHeading: 'The model',
  modelBody: [
    'The Brain Cancer Fund is open to every donor — individuals giving what they can, philanthropists backing specific research streams, and corporates committing to multi-year support. Every gift pools into a single research grants pool.',
    'The fund directs capital to two priorities: the validation of NextOnVera, a clinical decision-support platform for oncology teams; and early-stage, translational brain cancer research — the innovative, high-risk work that government and industry funding won’t back until someone else has de-risked it first. Multi-year donations provide the certainty researchers need to plan, hire, and run trials without stopping to reapply every twelve months. Capital is governed through Tour de Cure’s existing scientific assessment and grants process — independent review, milestone-based reporting, and transparent outcomes.'
  ],
  modelPoints: [
    {
      title: 'Every gift counts',
      body: 'Whether it is $50 from an individual or $200,000 a year from a corporate partner, every gift pools into the same research grants pool — backing NextOnVera and early-stage, high-risk research that traditional funding won’t touch.'
    },
    {
      title: 'Multi-year certainty',
      body: 'Multi-year donations give researchers the confidence to plan beyond a single grant cycle — hire teams, run trials, and reach milestones without the constant pressure of reapplying for funding every twelve months.'
    },
    {
      title: 'Tour De Cure governance',
      body: 'The founding charity already funds 1,250+ research projects. NextOnVera sits inside that proven machinery — not beside it as a side petition.'
    },
    {
      title: 'Milestone reporting',
      body: 'Disbursements are tied to established review gates so donors can see traction without swamping teams in vanity theatre.'
    }
  ],
  whatMakesDifferentHeading: 'What makes this different',
  whatMakesDifferent: [
    {
      title: 'Phase 0–1 focus',
      body: 'Most funding targets late-stage trials. We fund the earliest, riskiest work that de-risks promising science before industry will invest.'
    },
    {
      title: 'Agile & targeted',
      body: 'Instead of large, slow grants, we fund focused projects with clear milestones and faster decision cycles.'
    },
    {
      title: 'Built-in collaboration',
      body: 'Each project connects researchers, clinicians, and technology partners from day one — no silos.'
    },
    {
      title: 'Tech-sector leadership',
      body: 'Founded by a technology executive who understands systems, data, and speed — bringing that mindset to medical research.'
    }
  ],
  priorityAreasHeading: 'Funding priority areas',
  priorityAreas: [
    {
      title: 'Genomic & molecular profiling',
      body: 'Expanding access to molecular testing so treatment decisions match each tumour’s biology.'
    },
    {
      title: 'Drug repurposing',
      body: 'Identifying existing, approved drugs that may work against brain cancer — faster, cheaper pathways.'
    },
    {
      title: 'Blood-brain barrier delivery',
      body: 'Researching how to get therapies past the brain’s natural defences.'
    },
    {
      title: 'AI & computational modelling',
      body: 'Using AI to accelerate diagnosis, treatment matching, and trial design.'
    },
    {
      title: 'Patient quality of life',
      body: 'Tools and programmes that improve daily life for patients and carers, not just lab outcomes.'
    },
    {
      title: 'Clinical trial acceleration',
      body: 'Funding the infrastructure to run more trials, faster, with broader patient access.'
    }
  ],
  whatFundingDeliversHeading: 'What funding delivers',
  whatFundingDelivers: [
    'More research projects funded — not 90% rejection rates, but real projects moving forward',
    'Faster progress — from idea to evidence in years, not decades',
    'Better tools: NextOnVera, Grace AI and Cancer Navigator developed, validated and, subject to regulatory approval, made available where they are needed',
    'Stronger community — patients, families, clinicians, and researchers working together',
    'Evidence-based policy — data and outcomes that influence national research priorities',
    'A replicable model — if this works for brain cancer, it works for other underfunded diseases'
  ],
  whyFiveYears:
    'Multi-year donations give funded projects time to reach meaningful milestones — from early discovery to human-trial readiness. They give researchers the confidence of a stable, committed grants pool without the overhead of reapplying every twelve months. Whether an individual commits to three years of monthly giving or a corporate pledges a multi-year partnership, that certainty is what lets bold science actually happen.',
  trustHeading: 'Governance & Trust',
  trustBody:
    'Tour De Cure is a registered Australian charity with more than twenty years assessing and supporting cancer research. Independent scientific review, deductible-gift status, and public reporting sit behind every dollar that moves through this fund.',
  trustSignals: [
    'Independent scientific assessment via Tour De Cure’s research committee',
    'Milestone-based reporting for funded programmes',
    'Registered charity with deductible gift recipient (DGR) endorsement',
    'Transparent public narrative — progress shared without promising cures'
  ],
  disclaimer:
    'Named donor recognition and corporate partner logos are published only after explicit donor and Tour De Cure sign-off. The model welcomes gifts of every size — no minimum, no tier structure, no gatekeeping.',
  ctas: pathways
} as const;

export const getInvolvedPage = {
  documentTitle: 'Get Involved · NextOnVera',
  eyebrow: 'Get involved',
  title: 'Give, commit, or advocate',
  lead:
    'Give to the Brain Cancer Fund at any level, commit to multi-year donations that give researchers certainty, or lend your voice to advocacy. Every donor is celebrated — from the individual giving $50 to the corporate backing a multi-year research stream. Every pathway is led by Tour de Cure.',
  registerHeading: 'Register interest',
  registerBody:
    'Tell us how you’d like to help. Messages go to the NextOnVera team at Tour De Cure via a prepared email (managed inbox can be wired later without changing this form).'
} as const;

export const contactPage = {
  documentTitle: 'Contact · NextOnVera',
  eyebrow: 'Contact',
  title: 'Talk to the team',
  lead: `Reach NextOnVera through Tour De Cure. Email ${TDC_CONTACT_EMAIL}, or use the form below and we’ll open a prepared message in your email client.`,
  email: TDC_CONTACT_EMAIL
} as const;

export const aboutPage = {
  documentTitle: 'About · NextOnVera',
  eyebrow: 'About',
  title: 'About NextOnVera',
  lead:
    'NextOnVera is a Tour de Cure-led initiative funding technology-driven research and patient support to break a multi-decade stall in brain cancer survival.',
  body: [
    'It is steered as a serious medical-research programme — not a marketing campaign. The Brain Cancer Fund is open to every donor — individuals, philanthropists, and corporates — backing the validation of NextOnVera, a clinical decision-support platform, alongside early-stage, innovative, high-risk brain cancer research that falls outside traditional funding streams. Multi-year donations give researchers the certainty to plan bold work. We celebrate the generosity of every donor, at every level. Cancer Navigator and Grace AI complete the technology effort. All funding is governed by Tour de Cure’s scientific assessment process.'
  ]
} as const;
